import {normalize} from '@/lib/utils'

/** El manifiesto le gana al QR (`bateria_dispatch`), así que solo se propone lo inequívoco: cédula en
 * el nombre, o dos palabras y el mejor estricto en ambas direcciones; ante un empate, nada. */

export type PersonaDelMapeo = {id: string; nombre: string; cedula: string}

/** Contadas, inflarían la cobertura de un archivo que solo trae «de la». */
const PARTICULAS = new Set(['de', 'del', 'la', 'las', 'los', 'y'])
const MINIMO_PALABRAS = 2
const MINIMA_COBERTURA = 0.5
/** Una cédula en el nombre pesa más que cualquier cantidad de palabras coincidentes. */
const NIVEL_CEDULA = 1000
const CEDULA_MINIMA = 5

type Puntaje = {cedula: boolean; comunes: number; cobertura: number}

function trocear(texto: string): string[] {
  return normalize(texto)
    .replace(/[^a-z0-9]+/g, ' ')
    .split(' ')
    .filter((palabra) => palabra.length >= 2 && !PARTICULAS.has(palabra))
}

function palabrasDeArchivo(nombre: string): Set<string> {
  return new Set(trocear(nombre.replace(/\.[^.]*$/, '')))
}

function palabrasDePersona(persona: PersonaDelMapeo): string[] {
  return [...new Set(trocear(persona.nombre))]
}

function puntuar(persona: PersonaDelMapeo, palabrasArchivo: Set<string>): Puntaje {
  const propias = palabrasDePersona(persona)
  const comunes = propias.filter((palabra) => palabrasArchivo.has(palabra)).length
  const digitos = persona.cedula.replace(/\D/g, '')
  return {
    cedula: digitos.length >= CEDULA_MINIMA && palabrasArchivo.has(digitos),
    comunes,
    cobertura: propias.length === 0 ? 0 : comunes / propias.length,
  }
}

function elegible(puntaje: Puntaje): boolean {
  return puntaje.cedula || (puntaje.comunes >= MINIMO_PALABRAS && puntaje.cobertura >= MINIMA_COBERTURA)
}

function nivel(puntaje: Puntaje): number {
  return (puntaje.cedula ? NIVEL_CEDULA : 0) + puntaje.comunes
}

/** Nunca esconde ninguno: el archivo de la persona puede traer un nombre que el puntaje no premia. */
export function ordenarCandidatos(persona: PersonaDelMapeo, archivos: string[]): string[] {
  return archivos
    .map((nombre) => ({nombre, puntaje: puntuar(persona, palabrasDeArchivo(nombre))}))
    .sort(
      (a, b) =>
        nivel(b.puntaje) - nivel(a.puntaje) ||
        b.puntaje.cobertura - a.puntaje.cobertura ||
        a.nombre.localeCompare(b.nombre)
    )
    .map((candidato) => candidato.nombre)
}

type Par = {persona: string; archivo: string; nivel: number}

/** Un empate en el nivel más alto deja el grupo sin ganador. */
function ganadoresUnicos(pares: Par[], clave: (par: Par) => string, valor: (par: Par) => string) {
  const mejores = new Map<string, {valor: string; nivel: number; empatado: boolean}>()
  for (const par of pares) {
    const actual = mejores.get(clave(par))
    if (!actual || par.nivel > actual.nivel) {
      mejores.set(clave(par), {valor: valor(par), nivel: par.nivel, empatado: false})
    } else if (par.nivel === actual.nivel) {
      actual.empatado = true
    }
  }
  const ganadores = new Map<string, string>()
  for (const [grupo, mejor] of mejores) if (!mejor.empatado) ganadores.set(grupo, mejor.valor)
  return ganadores
}

/** `paquete_id → nombre de archivo`, solo con los pares inequívocos. */
export function proponerMapeo(personas: PersonaDelMapeo[], archivos: string[]): Record<string, string> {
  const troceados = archivos.map((nombre) => ({nombre, palabras: palabrasDeArchivo(nombre)}))
  const pares: Par[] = []
  for (const persona of personas) {
    for (const archivo of troceados) {
      const puntaje = puntuar(persona, archivo.palabras)
      if (elegible(puntaje)) pares.push({persona: persona.id, archivo: archivo.nombre, nivel: nivel(puntaje)})
    }
  }
  const porPersona = ganadoresUnicos(
    pares,
    (par) => par.persona,
    (par) => par.archivo
  )
  const porArchivo = ganadoresUnicos(
    pares,
    (par) => par.archivo,
    (par) => par.persona
  )
  const mapeo: Record<string, string> = {}
  for (const [persona, archivo] of porPersona) if (porArchivo.get(archivo) === persona) mapeo[persona] = archivo
  return mapeo
}
